import React, { forwardRef, useEffect, useMemo, useState } from "react";
import {
  CardNumberProps,
  CardTypes,
  MatchingPattern,
} from "../types/card-number.types";
import { TextInputBase } from "../../text-input/text-input-base.jsx";
import {
  findMatchingCreditCard,
  getCardTypes,
  getMatchingMetaData,
  runLuhnTest,
} from "../util/card-number.util";
import classnames from "classnames";

export interface CardNumberWithForwardRef
  extends React.ForwardRefExoticComponent<
    CardNumberProps & React.RefAttributes<HTMLInputElement>
  > {}

export const CardNumber = forwardRef<HTMLInputElement, CardNumberProps>(
  (
    {
      name = "card-number",
      disableAllCardDefaults = false,
      cardTypes: cardTypesProp,
      className: classNameProp,
      value: valueProp,
      onChange,
      onBlur,
      label = "Credit Card Number",
      setCvvLength,
      disableCardDefaults,
      defaultValue,
      hideError,
      runLuhnValidate,
      ...props
    },
    ref
  ) => {
    const [value, setValue] = useState(valueProp || defaultValue);
    const [wasPasted, setWasPasted] = useState(false);

    useEffect(() => {
      // Reset the value when no value found
      if (!valueProp && !defaultValue) {
        if (wasPasted) {
          return;
        }
        setValue("");
        return;
      }

      if (value !== defaultValue || value !== valueProp) {
        // Or condition required to handle controlled, uncontrolled scenarios when value is pasted into the field
        setValue(valueProp || defaultValue || value);
      }
    }, [valueProp, value, defaultValue, wasPasted]);

    const cardTypes: CardTypes | null = useMemo(
      () =>
        getCardTypes(
          disableAllCardDefaults,
          cardTypesProp,
          disableCardDefaults
        ),
      [disableAllCardDefaults, cardTypesProp, disableCardDefaults]
    );

    if (cardTypes === null) {
      return null;
    }

    const matchedCreditCard = value
      ? findMatchingCreditCard(cardTypes, value)
      : undefined;

    const {
      placeholder,
      pattern,
      minLength,
      maxLength,
      patternRegex,
    }: MatchingPattern = getMatchingMetaData(
      cardTypes,
      matchedCreditCard,
      value
    );

    const classes = classnames("spark-payment--card-number", {
      classNameProp,
    });

    const cvvLength = matchedCreditCard ? cardTypes[matchedCreditCard]?.cvv : 3;

    const onChangeHandler = (
      _: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      v: string
    ): void => {
      onChange?.(_, v);
      setWasPasted(false);
      // Using setCvvLength?. for storybook purpose only.
      setCvvLength?.(cvvLength);
    };

    const onBlurHandler = (
      e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
    ): void => {
      onBlur?.(e);
      // Using setCvvLength?. for storybook purpose only.
      setCvvLength?.(cvvLength);
    };

    const dataAttrCard = matchedCreditCard
      ? `cc-${matchedCreditCard}`
      : undefined;

    const pasteHandler = (e: React.ClipboardEvent): void => {
      const text = e.clipboardData.getData("Text");
      setWasPasted(true);
      setValue(text);

      // @ts-ignore
      onChange?.(e, text);

      if (ref) {
        // @ts-ignore
        ref.current.value = text;
      }
    };

    const isUncontrolled = valueProp === undefined;
    const { validate } = props;

    return (
      <TextInputBase
        name={name}
        type={"phone"}
        role={"textbox"}
        label={label}
        {...props}
        validate={runLuhnValidate ? runLuhnTest : validate}
        typeaheadFormat={pattern}
        isTypeahead={true}
        className={classes}
        ref={ref}
        isCreditCard={true}
        placeHolder={placeholder}
        cardType={matchedCreditCard}
        minLength={minLength}
        maxLength={maxLength}
        validateOnChange={true}
        validateOnBlur={true}
        validateOnMount={true}
        validatePattern={patternRegex}
        value={!isUncontrolled ? value || "" : undefined}
        onChange={onChangeHandler}
        wrapperDataAttr={{ "data-card-type": dataAttrCard }}
        unifiedFields={hideError}
        onPaste={pasteHandler}
        defaultValue={isUncontrolled ? value : undefined}
        onBlur={onBlurHandler}
      />
    );
  }
) as CardNumberWithForwardRef;

CardNumber.displayName = "CardNumber";
